import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import Table from "components/DesignSystem/Table/V2/Table";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { PlusIcon } from "components/icons/PlusIcon";
import { DeleteIcon } from "components/icons/delete";
import { useFormikContext, getIn, Formik, FieldArray, Form } from "formik";
import { array, object, string } from "yup";
import { useToast } from "hooks/useToast";
import { useSelector, useDispatch } from "react-redux";
import {
  CellContext,
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "react-table-8.10.7";
import { actionItemsApi } from "store/apis/actionItems";
import {
  useGetAllDocumentTypesQuery,
  useGetDocumentRequestQuery,
  useCreateDocumentRequestMutation,
  useUpdateDocumentRequestMutation,
  DocumentRequestPayload,
  useGetLinkableFinancialClosingsQuery,
} from "store/apis/requestDocument";
import {
  toggleDocumentRequestEdit,
  openRequestDocumentsModal,
} from "store/slices/requestDocumentsInChat";
import { RootState } from "store/store";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { useState } from "react";
import { LinkFinancialClosingStep } from "./LinkFinancialClosingStep";

const OTHER_DOCUMENT = "OTHER_DOCUMENT";

const requestDocumentSchema = array().of(
  object().shape({
    other_name: string().when("document_type_id", {
      is: OTHER_DOCUMENT,
      then: (schema) => schema.required("Please enter document name"),
    }),
  })
);

const DOCUMENT_TYPE_FIELD = "document_type_id";
const DESCRIPTION_FIELD = "description";

type RequestFieldProps = { index: number };

type RequestRow = { name: string; description: string };

type RequestTableMeta = { onRemove: (index: number) => void };

type MobileDocumentRequestCardProps = {
  index: number;
  onRemove: (index: number) => void;
};

const DescriptionField = ({ index }: RequestFieldProps) => (
  <TextInput
    placeholder="Write a concise description here"
    name={`requests.${index}.${DESCRIPTION_FIELD}`}
  />
);

const DocumentTypeField = ({ index }: RequestFieldProps) => {
  const { values, setFieldValue } = useFormikContext();
  const groupId = useSelector(
    (state: RootState) => state.requestDocumentsInChat.groupId
  );
  const { data: documentTypes } = useGetAllDocumentTypesQuery(
    {
      groupId: groupId!,
    },
    { skip: !groupId }
  );

  const fieldName = `requests.${index}.${DOCUMENT_TYPE_FIELD}`;
  const customNameField = `requests.${index}.other_name`;
  const value = getIn(values, fieldName);
  const label = documentTypes?.find((d) => d.uuid === value)?.name;
  const isOtherDocument = value === OTHER_DOCUMENT;
  const comboboxValue = isOtherDocument
    ? { value: OTHER_DOCUMENT, label: "Other Document (Enter name)" }
    : value && { value, label };

  return (
    <div className="t-flex t-items-start t-gap-2 sm:t-flex-col sm:t-items-stretch">
      <div className="t-flex-1">
        <Combobox
          value={comboboxValue}
          menuPortalTarget={document.body}
          classNames={{
            /* @tw */
            menuPortal: () => "!t-z-dropdown t-pointer-events-auto",
          }}
          withForm
          name={fieldName}
          options={documentTypes?.map((d) => ({
            label: d.name,
            value: d.uuid,
          }))}
          // @ts-ignore
          actions={
            <div
              className="t-text-body-sm t-text-purple-55 t-font-medium t-cursor-pointer t-pb-3"
              onClick={() => {
                setFieldValue(fieldName, OTHER_DOCUMENT);
                (document.activeElement as HTMLElement)?.blur();
              }}
            >
              Other Document (Enter name)
            </div>
          }
        />
      </div>
      {isOtherDocument && (
        <div className="t-w-40 t-shrink-0 sm:t-w-full">
          <TextInput name={customNameField} placeholder="Enter name" required />
        </div>
      )}
    </div>
  );
};

const RemoveCell = (info: CellContext<RequestRow, unknown>) => {
  const { onRemove } = info.table.options.meta as RequestTableMeta;
  const handleRemove = () => onRemove(info.row.index);

  return (
    <div className="t-flex t-justify-end">
      <Button
        tabIndex={1}
        size="small"
        customType="ghost_icon"
        type="button"
        onClick={handleRemove}
      >
        <DeleteIcon />
      </Button>
    </div>
  );
};

const columnHelper = createColumnHelper<RequestRow>();

const columns = [
  columnHelper.accessor("name", {
    id: DOCUMENT_TYPE_FIELD,
    header: "Document Name",
    cell: (info) => <DocumentTypeField index={info.row.index} />,
    size: 30,
  }),
  columnHelper.accessor("description", {
    header: "Description",
    cell: (info) => <DescriptionField index={info.row.index} />,
    size: 65,
  }),
  columnHelper.display({
    id: "action",
    header: "",
    cell: RemoveCell,
    size: 5,
  }),
];

const MobileDocumentRequestCard = ({
  index,
  onRemove,
}: MobileDocumentRequestCardProps) => {
  const handleRemove = () => onRemove(index);

  return (
    <div className="t-flex t-flex-col t-gap-3 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-3">
      <div className="t-flex t-flex-col t-gap-1.5">
        <div className="t-flex t-items-center t-justify-between">
          <span className="t-text-body-sm t-text-text-30">Document Name</span>
          <Button
            size="small"
            customType="ghost_icon"
            type="button"
            onClick={handleRemove}
          >
            <DeleteIcon />
          </Button>
        </div>
        <DocumentTypeField index={index} />
      </div>
      <div className="t-flex t-flex-col t-gap-1.5">
        <span className="t-text-body-sm t-text-text-30">Description</span>
        <DescriptionField index={index} />
      </div>
    </div>
  );
};

export const DocumentRequests = ({
  remove,
}: {
  remove: (index: number) => void;
}) => {
  const { values } = useFormikContext<{ requests: RequestRow[] }>();
  const isSmallScreen = useIsSmallScreen();

  const table = useReactTable({
    data: values.requests || [],
    columns,
    meta: { onRemove: remove },
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 5,
      maxSize: 100,
    },
  });

  if (isSmallScreen) {
    return (
      <div className="t-flex t-flex-col t-gap-3">
        {(values.requests || []).map((_, index) => (
          <MobileDocumentRequestCard
            key={index}
            index={index}
            onRemove={remove}
          />
        ))}
      </div>
    );
  }

  return (
    <Table.Container>
      <Table.Content>
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.HeadCell
                  key={header.id}
                  style={{
                    width: `${header.getSize()}%`,
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </Table.HeadCell>
              ))}
            </Table.Row>
          ))}
        </Table.Head>
        <Table.Body>
          {table.getRowModel().rows.map((row) => (
            <Table.Row key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Table.Cell
                  key={cell.id}
                  style={{
                    width: `${cell.column.getSize()}%`,
                    verticalAlign: "top",
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Content>
    </Table.Container>
  );
};

export const CreateEditMode = ({ requestId }: { requestId?: string }) => {
  const { successToast, alertToast } = useToast();

  const isOpen = useSelector(
    (state: RootState) => state.requestDocumentsInChat.isModalOpen
  );
  const channelId = useSelector(
    (state: RootState) => state.requestDocumentsInChat.channelId
  );
  const groupId = useSelector(
    (state: RootState) => state.requestDocumentsInChat.groupId
  );

  const { data: documentsRequest, isLoading } = useGetDocumentRequestQuery(
    {
      groupId: groupId!,
      requestId: requestId!,
    },
    { skip: !groupId || !requestId, refetchOnMountOrArgChange: true }
  );

  const {
    data: linkableClosings = [],
    isFetching: isLoadingClosings,
    isError: isClosingsError,
    refetch: refetchClosings,
  } = useGetLinkableFinancialClosingsQuery(
    { groupId: groupId!, channelId: channelId! },
    {
      skip: Boolean(requestId) || !groupId || !channelId,
      refetchOnMountOrArgChange: true,
    }
  );
  const [selectedClosingId, setSelectedClosingId] = useState<string | null>(
    null
  );
  const [isClosingStepDone, setIsClosingStepDone] = useState(false);
  const hasLinkableClosings = !requestId && linkableClosings.length > 0;
  // a failed closings load stays on the link step so it is never skipped silently
  const showLinkClosingStep =
    !requestId &&
    !isClosingStepDone &&
    (isLoadingClosings || isClosingsError || linkableClosings.length > 0);

  const [createDocumentRequest, { isLoading: isCreating }] =
    useCreateDocumentRequestMutation();
  const [updateDocumentRequest, { isLoading: isUpdating }] =
    useUpdateDocumentRequestMutation();

  const dispatch = useDispatch();

  const closeModal = () => {
    if (requestId) {
      return dispatch(toggleDocumentRequestEdit(false));
    }
    dispatch(
      openRequestDocumentsModal({ isModalOpen: false, channelId: null })
    );
  };

  const onUpdate = async ({
    requests,
  }: {
    requests: DocumentRequestPayload["request_data"];
  }) => {
    try {
      if (channelId && groupId && requestId) {
        await updateDocumentRequest({
          groupId: groupId,
          requestId,
          payload: {
            request_data: requests,
          },
        }).unwrap();
        successToast({ message: "Request has been edited successfully!" });
        closeModal();
        setTimeout(() => {
          dispatch(
            actionItemsApi.util.invalidateTags([{ type: "ALL_ACTION_ITEMS" }])
          );
        }, 1000);
      }
    } catch (error) {
      alertToast({ message: "Failed to edit the request!" });
    }
  };

  const onCreate = async ({
    requests,
  }: {
    requests: DocumentRequestPayload["request_data"];
  }) => {
    try {
      if (channelId && groupId) {
        await createDocumentRequest({
          groupId: groupId,
          payload: {
            channel_id: channelId,
            request_data: requests,
            ...(selectedClosingId
              ? { financial_closing_id: selectedClosingId }
              : {}),
          },
        }).unwrap();
        successToast({ message: "Request has been sent successfully!" });
        closeModal();
        setTimeout(() => {
          dispatch(
            actionItemsApi.util.invalidateTags([{ type: "ALL_ACTION_ITEMS" }])
          );
        }, 1000);
      }
    } catch (error) {
      alertToast({ message: "Failed to send the request!" });
    }
  };

  if (isLoading) {
    return (
      <div className="t-flex t-justify-center t-items-center">
        <Loader size="small" />
      </div>
    );
  }

  if (showLinkClosingStep) {
    return (
      <LinkFinancialClosingStep
        closings={linkableClosings}
        isLoading={isLoadingClosings}
        isError={isClosingsError}
        selectedClosingId={selectedClosingId}
        onSelect={setSelectedClosingId}
        onNext={() => setIsClosingStepDone(true)}
        onSkip={() => {
          setSelectedClosingId(null);
          setIsClosingStepDone(true);
        }}
        onRetry={refetchClosings}
        onCancel={closeModal}
      />
    );
  }

  return (
    <Modal.Root open={isOpen} onOpenChange={closeModal}>
      <Formik
        initialValues={{
          requests: documentsRequest?.requested_documents || [
            {
              document_type_id: "",
              description: "",
              other_name: "",
            },
          ],
        }}
        validationSchema={object().shape({
          requests: requestDocumentSchema,
        })}
        onSubmit={requestId ? onUpdate : onCreate}
      >
        {({ submitForm }) => (
          <Modal.Content size="xxl">
            <Modal.Header>
              <Modal.Title>Request Documents</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body className="sm:t-px-4">
              <div>
                <Form className="t-m-0">
                  <FieldArray name="requests">
                    {(arrayHelpers) => (
                      <div className="t-flex t-flex-col t-gap-2">
                        <DocumentRequests remove={arrayHelpers.remove} />
                        <div className="t-self-start t-pl-3 sm:t-pl-0">
                          <Button
                            type="button"
                            customType="secondary"
                            size="small"
                            onClick={arrayHelpers.push.bind(this, {
                              document_type_id: "",
                              description: "",
                              other_name: "",
                            })}
                          >
                            <div className="t-flex t-items-center t-gap-1 t-text-text-30">
                              <PlusIcon size="10" />
                              <span className="t-text-text-60">Add more</span>
                            </div>
                          </Button>
                        </div>
                      </div>
                    )}
                  </FieldArray>
                </Form>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="t-flex t-justify-end t-gap-2">
                {hasLinkableClosings ? (
                  <Button
                    type="button"
                    onClick={() => setIsClosingStepDone(false)}
                  >
                    Back
                  </Button>
                ) : (
                  <Button type="button" onClick={closeModal}>
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={submitForm}
                  customType="primary"
                  isLoading={isCreating || isUpdating}
                  disabled={isCreating || isUpdating}
                >
                  {requestId ? "Save" : "Send"}
                </Button>
              </div>
            </Modal.Footer>
          </Modal.Content>
        )}
      </Formik>
    </Modal.Root>
  );
};
